import React, { useRef, useEffect } from 'react'
import { graphql } from 'gatsby'
import Navbar from 'src/modules/organisms/navbar/Navbar'
import Footer from 'src/modules/organisms/footer/Footer'
import Layout from 'src/components/partials/Layout'
import SEO from 'src/components/partials/Seo'
import * as styles from './Home.module.scss'
import ComponentTitleLockup from 'src/modules/molecules/component-title-lockup'
import ClientGrid from 'src/modules/organisms/client-grid'
import ContentEmbeddedTwoUpGrid from 'src/modules/organisms/content-embedded-two-up-grid'
import TwoUpGrid from 'src/modules/organisms/two-up-grid'
import ProductCard from 'src/modules/molecules/product-card'
import TitleLockup from 'src/modules/molecules/title-lockup'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'
import { Navigation, EffectFade } from 'swiper/modules'
import { ReactSVG } from 'react-svg'
import { Button } from 'src/modules/molecules/button'
import HoverCards from 'src/modules/organisms/hover-cards'
import { WistiaPlayer } from 'src/modules/molecules/wistia-embed/player'
import StickyAnchorNav from 'src/modules/molecules/sticky-anchor-nav'
import { BannerBar } from 'src/components/blocks/BannerBar'
import { SectionContainer } from 'src/modules/molecules/section'

const clientLogos = [
  {
    title: 'Public',
    logos: [
      { src: '/assets/logos/clients/ADP.svg', description: ['$ADP'] },
      { src: '/assets/logos/clients/Hubspot.svg', description: ['$HUBS'] },
      { src: '/assets/logos/clients/Sage.svg', description: ['$SGE'] },
      { src: '/assets/logos/clients/Toast.svg', description: ['$TOST'] },
      { src: '/assets/logos/clients/Amazon.svg', description: ['$AMZN'] },
      {
        src: '/assets/logos/clients/Blackbaud.svg',
        description: ['$BLKB']
      },
      {
        src: '/assets/logos/clients/Omnicom-group.svg',
        description: ['$OMC']
      },
      {
        src: '/assets/logos/clients/Pernod-ricard.svg',
        description: ['$PDRDF']
      }
    ]
  },
  {
    title: 'Growth',
    logos: [
      {
        src: '/assets/logos/clients/Clickup.svg',
        description: ['Series C']
      },
      {
        src: '/assets/logos/clients/Flexport.svg',
        description: ['Series E ', 'YC Winter 2014']
      },
      {
        src: '/assets/logos/clients/Deel.svg',
        description: ['Series D ', 'YC Winter 2019']
      },
      {
        src: '/assets/logos/clients/Oura.svg',
        description: ['Series D ', 'YC Winter 2016']
      },
      {
        src: '/assets/logos/clients/Deputy.svg',
        description: ['Series B']
      },
      {
        src: '/assets/logos/clients/buildops.svg',
        description: ['Series C']
      },
      {
        src: '/assets/logos/clients/Modern-treasury.svg',
        description: ['Series C ', 'YC Summer 2018']
      },
      {
        src: '/assets/logos/clients/Zeal.svg',
        description: ['Series B ', 'YC Summer 2019']
      }
    ]
  },
  {
    title: 'Startup',
    logos: [
      { src: '/assets/logos/clients/Default.svg', description: ['Seed stage'] },
      { src: '/assets/logos/clients/Folk.svg', description: ['Seed stage'] },
      {
        src: '/assets/logos/clients/Givebutter.svg',
        description: ['Series A']
      },
      { src: '/assets/logos/clients/&Open.svg', description: ['Series A'] },
      {
        src: '/assets/logos/clients/Harmonic.svg',
        description: ['Series A']
      },
      {
        src: '/assets/logos/clients/Memberstack.svg',
        description: ['Seed stage', 'YC Summer 2020']
      },
      {
        src: '/assets/logos/clients/Outseta.svg',
        description: ['Seed stage', 'YC Winter 2017']
      },
      { src: '/assets/logos/clients/Galley.svg', description: ['Series A'] }
    ]
  }
]

const HomePage = ({ data }: { data: any }) => {
  const prevRef = useRef<HTMLButtonElement | null>(null)
  const nextRef = useRef<HTMLButtonElement | null>(null)
  const swiperRef = useRef<any>(null)

  useEffect(() => {
    if (prevRef.current && nextRef.current) {
      const swiperElement = document.querySelector(
        '.client-reviews-swiper'
      ) as HTMLElement & {
        swiper: any
      }
      const swiper = swiperElement?.swiper
      if (swiper) {
        swiperRef.current = swiper
        swiper.params.navigation.prevEl = prevRef.current
        swiper.params.navigation.nextEl = nextRef.current
        swiper.navigation.init()
        swiper.navigation.update()
      }
    }
  }, [])

  const goToSlide = (index: number) => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(index)
    }
  }

  return (
    <Layout>
      {data.allContentfulAnnouncementBar &&
        data.allContentfulAnnouncementBar.edges
          .filter(({ node }) => !node.secondary) // Filter out secondary banners
          .map(({ node }) => (
            <React.Fragment key={node.id}>
              {node.showBanner && (
                <BannerBar
                  btnText={node.buttonText}
                  btnUrl={node.buttonUrl}
                  copy={node.copy}
                  showCloseButton={false}
                  title={node.title}
                />
              )}
            </React.Fragment>
          ))}
      {/* add a prop to navbar if announcement bar is open */}
      <Navbar
        announcementBarOpen={data.allContentfulAnnouncementBar.edges.some(
          ({ node }) => node.showBanner
        )}
      />
      <StickyAnchorNav
        links={[
          { title: 'Extract', href: '#extract' },
          { title: 'Prepare', href: '#prepare' },
          { title: 'Map', href: '#map' },
          { title: 'Transform & Validate', href: '#transform-validate' },
          { title: 'Build', href: '#build' }
        ]}
      />
      <main className={styles.home}>
        <section className={styles.hero}>
          <ComponentTitleLockup
            headingLevel={'h1'}
            visualStyle={'h1'}
            title='AI-powered'
            titleAlt='data migration'
            bodyText1='The best way for enterprises to prepare, map, clean, transform, and validate the data that powers your business.'
            maxWidth='sm'
            align='center'
          ></ComponentTitleLockup>
          <Button href='/demo' variant='secondary'>
            Book a demo
          </Button>
          <div className={styles.video}>
            <div className={styles.videoContainer}>
              <WistiaPlayer videoId='djx1zwd5u6' noMargin />
            </div>
          </div>
        </section>

        <section className={styles.clients}>
          <div className={styles.clientsHeader}>
            <ComponentTitleLockup
              title='Powering 3,000+ of the'
              titleAlt=' world’s most effective teams'
              visualStyle='h3'
              headingLevel='h2'
            />
          </div>
          {clientLogos.map((client, index) => (
            <div key={index} className={styles.clientsRow}>
              <h3>{client.title}</h3>
              <hr className={styles.mobile} />
              <ClientGrid
                logos={client.logos}
                hideTitle
                showDescriptionsOnHover
                tieredLayout
              />
            </div>
          ))}
        </section>

        <ContentEmbeddedTwoUpGrid
          id='extract'
          bgColor='gray'
          titleProps={{
            title: 'Get structured data',
            titleAlt: 'from any file',
            visualStyle: 'h3',
            headingLevel: 'h3',
            align: 'center'
          }}
          description='Flatfile supports file-based data collection from virtually anywhere—uploads, APIs, webhooks, databases, cloud storage, and direct integration with popular file storage solutions.'
          gridItems={[
            {
              title: 'Smart Extractor',
              mediaSrc: '/assets/mockups/extractor-agent.svg',
              altText: 'Placeholder Image 1',
              content:
                'Even when source data is highly variable and unstructured, Flatfile’s extractor agent can automatically detect file structure, parse content, and create a workbook within Flatfile for further data preparation.'
            },
            {
              title: 'Source File Types',
              mediaSrc: '/assets/mockups/source-files.svg',
              altText: 'Placeholder Image 2',
              content:
                'Flatfile can ingest data from a variety of file types. Flatfile then organizes the data and creates a database with a user-friendly interface.'
            }
          ]}
        />
        <div className={styles.grids}>
          <TwoUpGrid
            id='prepare'
            mediaType='image'
            mediaSrc='/assets/mockups/data-preperation.jpg'
            altText='Data Preparation Product Mockup'
            title='Save hours'
            titleAlt='on data preparation'
            titleHeadingLevel='h4'
            titleVisualStyle='h3'
            content='Flatfile sets the standard for how data preparation should feel—flexible, intelligent, and effortless. Instead of forcing customers into rigid templates, our AI agent understands your schema and reshapes inbound files to match. Whether adding or removing columns, splitting and merging fields, or unpivoting complex datasets, Flatfile adapts to your requirements, making messy files mappable.'
            layout='simple'
            padding='none'
            reverse={true}
            // button={
            //   <Button href='/platform/use-cases/data-migration/' showTrailIcon>
            //     Find out more
            //   </Button>
            // }
          />
          <TwoUpGrid
            id='map'
            mediaType='image'
            mediaSrc='/assets/mockups/data-mapping.jpg'
            altText='Data Mapping Product Mockup'
            title='Map your data'
            titleAlt='accurately in seconds'
            titleHeadingLevel='h4'
            titleVisualStyle='h3'
            content='Flatfile’s AI mapping engine aligns incoming data to your schema—field by field, no matter how inconsistent the source file is. It doesn’t just look at column names; it understands what the data means, draws from billions of prior decisions, and gets smarter with every import.'
            padding='none'
            layout='simple'
            button={
              <Button href='/platform/mapping/' target='_blank' showTrailIcon>
                Learn more about mapping
              </Button>
            }
          />
        </div>
        <section className={styles.productCards} id='transform-validate'>
          <SectionContainer>
            <ComponentTitleLockup
              title='Automatically enforce'
              titleAlt='data standards'
              bodyText1='Once data lands in a Flatfile workbook, it’s instantly checked against your rules, with errors highlighted for quick resolution.'
              maxWidth='sm'
              visualStyle='h3'
              headingLevel='h3'
              align='left'
              layout='twoColumnButton'
              buttonProps={{
                href: '/product/transform/',
                variant: 'primary',
                children: 'Learn about Transform',
                showTrailIcon: true
              }}
            ></ComponentTitleLockup>
          </SectionContainer>
          <div className={styles.productCardsGrid}>
            <ProductCard
              image='/assets/mockups/ai-transform-4x.png'
              title='Validation Rules'
              bodyCopy='Flatfile enforces your data requirements with real-time validation—flagging errors like missing fields, incorrect formats, or unexpected values.'
              alignItems='flex-start'
            />
            <ProductCard
              image='/assets/mockups/auto-fix.svg'
              title='AutoFix'
              bodyCopy='Watch as Flatfile resolves errors autonomously. Click once and AutoFix will correct any formatting errors, then present the changes for review and approval.'
              alignItems='flex-start'
            />
            <ProductCard
              image='/assets/mockups/validation-rules-4x.png'
              title='AI Transform'
              bodyCopy='AI Transform lets you adapt data at scale using simple prompts. Describe what you want changed, and Flatfile instantly applies those edits across your dataset.'
              alignItems='flex-start'
            />
          </div>
        </section>
        <section className={styles.clientReviews}>
          <TitleLockup
            title='The Flatfile effect—'
            titleAlt='according to our customers'
            headingLevel='h4'
            visualStyle='h3'
            align='center'
          ></TitleLockup>
          <Swiper
            modules={[Navigation, EffectFade]}
            effect='fade'
            fadeEffect={{ crossFade: true }}
            spaceBetween={50}
            slidesPerView={1}
            className='client-reviews-swiper'
          >
            <SwiperSlide>
              <div className={styles.clientReviewsSlide}>
                <div className={styles.clientReviewsSlideHeader}>
                  <div className={styles.clientReviewsSlideHeaderTitle}>
                    <p>Scott Patterson</p>
                    <p>Director of Implementation</p>
                  </div>
                  <div className={styles.clientReviewsSlideHeaderImg}>
                    <ReactSVG
                      src='/assets/logos/clients/Buildops.svg'
                      className={styles.clientReviewsSlideHeaderImgActive}
                      onClick={() => goToSlide(0)}
                    />
                    <ReactSVG
                      src='/assets/logos/clients/Kolleno.svg'
                      onClick={() => goToSlide(1)}
                    />
                    <ReactSVG
                      src='/assets/logos/clients/Growth-zone.svg'
                      onClick={() => goToSlide(2)}
                    />
                  </div>
                </div>
                <div className={styles.clientReviewsSlideBody}>
                  <h3>
                    "It’s one of the more important tools in our implementation
                    toolbox. Without tools like Flatfile, scaling would be
                    almost impossible."
                  </h3>
                  <div className={styles.clientReviewsSlideBodyStats}>
                    <div>
                      <h4>25%</h4>
                      <p>Decrease in per-project person hours</p>
                    </div>
                    <div>
                      <h4>20%</h4>
                      <p>Decrease in time to launch</p>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className={styles.clientReviewsSlide}>
                <div className={styles.clientReviewsSlideHeader}>
                  <div className={styles.clientReviewsSlideHeaderTitle}>
                    <p>Adonis Damalis</p>
                    <p>Head of Customer Success</p>
                  </div>
                  <div className={styles.clientReviewsSlideHeaderImg}>
                    <ReactSVG
                      src='/assets/logos/clients/Buildops.svg'
                      onClick={() => goToSlide(0)}
                    />
                    <ReactSVG
                      src='/assets/logos/clients/Kolleno.svg'
                      className={styles.clientReviewsSlideHeaderImgActive}
                      onClick={() => goToSlide(1)}
                    />
                    <ReactSVG
                      src='/assets/logos/clients/Growth-zone.svg'
                      onClick={() => goToSlide(2)}
                    />
                  </div>
                </div>
                <div className={styles.clientReviewsSlideBody}>
                  <h3>
                    "Flatfile's positive impact on productivity has been
                    apparent, as it empowers clients to easily upload
                    substantial data sets within minutes. This not only spares
                    our development team from shouldering the burden but also
                    frees them to concentrate on other essential tasks."
                  </h3>
                  <div className={styles.clientReviewsSlideBodyStats}>
                    <div>
                      <h4>70%</h4>
                      <p>Reduction in the time data import tasks take</p>
                    </div>
                    <div>
                      <h4>100%</h4>
                      <p>Success rate for customers using Flatfile</p>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className={styles.clientReviewsSlide}>
                <div className={styles.clientReviewsSlideHeader}>
                  <div className={styles.clientReviewsSlideHeaderTitle}>
                    <p>Tamara Larsen</p>
                    <p>Head of Implementation</p>
                  </div>
                  <div className={styles.clientReviewsSlideHeaderImg}>
                    <ReactSVG
                      src='/assets/logos/clients/Buildops.svg'
                      onClick={() => goToSlide(0)}
                    />
                    <ReactSVG
                      src='/assets/logos/clients/Kolleno.svg'
                      onClick={() => goToSlide(1)}
                    />
                    <ReactSVG
                      src='/assets/logos/clients/Growth-zone.svg'
                      className={styles.clientReviewsSlideHeaderImgActive}
                      onClick={() => goToSlide(2)}
                    />
                  </div>
                </div>
                <div className={styles.clientReviewsSlideBody}>
                  <h3>
                    “Now we are able to tell our customers (regardless of their
                    technical ability), ‘Give us your raw data and we’ll format
                    it for you.’ Flatfile has been truly empowering and
                    transformational for us!”
                  </h3>
                  <div className={styles.clientReviewsSlideBodyStats}>
                    <div>
                      <h4>2.17x</h4>
                      <p>Faster customer onboarding</p>
                    </div>
                    <div>
                      <h4>400%</h4>
                      <p>Net promoter score increase</p>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
            <div className='client-reviews-swiper__navigation'>
              <button ref={prevRef} className='prev'>
                <ReactSVG src='/assets/icons/left-arrow.svg' />
              </button>
              <button ref={nextRef} className='next'>
                <ReactSVG src='/assets/icons/right-arrow.svg' />
              </button>
            </div>
          </Swiper>
        </section>
        <section id='build'>
          <HoverCards
            titleLockupProps={{
              layout: 'twoColumnButton',
              headingLevel: 'h3',
              visualStyle: 'h3',
              title: 'Implement with or',
              titleAlt: 'without code',
              bodyText1:
                'Build the structure, logic, and constraints your data needs with the help of Flatfile’s configuration agent. Dive into code when you want to be more hands on.',
              maxWidth: 'md',
              buttonProps: {
                href: '/product/configuration',
                variant: 'primary',
                children: 'Learn about configuration',
                showTrailIcon: true
              }
            }}
            cards={[
              {
                title: 'Agentic configuration',
                paragraph:
                  "Flatfile’s configuration agent handles the work you'd normally hand off to a developer—from creating constraints and writing custom actions to installing plugins and updating your schema. It understands the structure of your data, applies logic in real time, and executes changes with precision. Backed by broad world knowledge and built-in context, it empowers non-technical teams to shape their Flatfile workspace with the same depth and control as writing code—only faster.",
                imageSrc: '/assets/mockups/agentic-config-4x.png',
                imageAlt: 'Configuration Agent Mockup'
              },
              {
                title: 'Highly extensible',
                paragraph:
                  'Flatfile is designed to fit into your stack—not the other way around. With an API-first architecture, AI-assisted workflows, and event-driven design, developers can customize every stage of the data import experience. Use our SDKs to embed Flatfile anywhere, tap into webhooks to trigger downstream processes, and manage everything through Git-based config for version-controlled deployments. Need something custom? Leverage our open-source plugin library or build your own. However you work, Flatfile extends with you.',
                imageSrc: '/assets/mockups/highly-extensible.svg',
                imageAlt: 'API Integration Mockup'
              },
              {
                title: 'End-to-end customization',
                paragraph:
                  'Build Mode lets you define not just the rules, but the entire experience around customer data onboarding. Define validation logic in natural language or code, add inline guides to help users stay on track, and set guardrails to prevent common data issues before they happen. You can customize everything from field-level instructions to required constraints, and apply your brand’s theme to create a familiar, polished interface. It’s a complete toolkit for shaping how customer data enters your system, on your terms.',
                imageSrc: '/assets/mockups/customization.svg',
                imageAlt: 'AI Tools Mockup'
              }
            ]}
          />
        </section>
        <section className={styles.productCards}>
          <ComponentTitleLockup
            title='Built for the most complex'
            titleAlt='and sensitive data'
            visualStyle='h3'
            headingLevel='h3'
            align='center'
          ></ComponentTitleLockup>
          <div className={styles.productCardsGrid}>
            <ProductCard
              image='/assets/mockups/data-structure-4x.jpg'
              title='Structured beneath the surface'
              bodyCopy='Flatfile supports nested fields, linked records, and multi-sheet relationships—bringing database-level structure to a familiar, spreadsheet-like interface.'
              alignItems='flex-start'
              link={{
                href: '/platform/workbooks',
                children: 'Learn more',
                showTrailIcon: true
              }}
            />
            <ProductCard
              image='/assets/mockups/human-approval.svg'
              title='Human-in-the-loop'
              bodyCopy='Flatfile keeps humans in control, with every AI or user-driven change surfaced for review. From manual edits to AI transformations, nothing goes live without a clear approval path.'
              alignItems='flex-start'
            />
            <ProductCard
              image='/assets/mockups/security.svg'
              title='Enterprise-grade security'
              bodyCopy='Flatfile protects your data at every step with encryption, access controls, and secure infrastructure—built to meet the needs of modern enterprises.'
              alignItems='flex-start'
              link={{
                href: '/security',
                children: 'Learn more',
                showTrailIcon: true
              }}
            />
          </div>
        </section>
      </main>
      <Footer
        showCta={true}
        ctaTitle='<h3>See how fast <span>clean data can be</span></h3>'
      />
    </Layout>
  )
}

export const query = graphql`
  query announcementBarEntryQuery {
    allContentfulAnnouncementBar {
      edges {
        node {
          buttonText
          buttonUrl
          copy
          id
          showBanner
          showCloseButton
          title
          secondary
        }
      }
    }
  }
`

export default HomePage

export const Head = () => (
  <SEO
    description='Flatfile is the AI data preparation and migration software that helps teams extract, prepare, map, transform, and validate messy data.'
    title='AI Data Preparation and Migration Software'
  />
)
